import React, { useEffect, useRef, useState } from "react";
import { AddIcon, EditIcon } from "../../icons";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import ReplayIcon from "@mui/icons-material/Replay";
import Cookies from "js-cookie";
import { GENERATE_AUDIO_BOOK } from "../../helper/apiurls/apiurls";
import "../../styles/common/contentToolbarWrapper.scss";

const cleanText = (text) => {
  if (!text) return "";

  return text
    .replace(/[#*_`>-]/g, "")
    .replace(/–|—/g, "-")
    .replace(/[^\x00-\x7F]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const splitTextSmart = (text, maxLength = 180) => {
  const words = text.split(" ");
  const chunks = [];
  let currentChunk = "";

  words.forEach((word) => {
    if ((currentChunk + word).length > maxLength) {
      if (currentChunk) chunks.push(currentChunk.trim());
      currentChunk = word + " ";
    } else {
      currentChunk += word + " ";
    }
  });

  if (currentChunk.trim()) {
    chunks.push(currentChunk.trim());
  }

  return chunks;
};

const StaticWaveform = ({ progress }) => {
  const canvasRef = useRef(null);
  const [peaks] = useState(() =>
    Array.from({ length: 45 }, () => Math.random() * 0.6 + 0.2),
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const width = canvas.width;
    const height = canvas.height;
    ctx.clearRect(0, 0, width, height);

    const barWidth = 3;
    const gap = 3;
    const totalBarWidth = barWidth + gap;
    const totalWidth = peaks.length * totalBarWidth;
    const startX = width - totalWidth > 0 ? (width - totalWidth) / 2 : 0;

    const playedColor = "#54656f";
    const unplayedColor = "#b0bec5";

    peaks.forEach((peak, i) => {
      const x = startX + i * totalBarWidth;
      const barHeight = height * peak;
      const y = (height - barHeight) / 2;

      const barProgress = i / peaks.length;
      ctx.fillStyle = barProgress < progress ? playedColor : unplayedColor;

      ctx.beginPath();
      ctx.roundRect(x, y, barWidth, barHeight, 2);
      ctx.fill();
    });
  }, [peaks, progress]);

  return (
    <canvas
      ref={canvasRef}
      className="waveform-canvas"
      width={160}
      height={30}
    />
  );
};

const WaveformPlayer = ({ audioUrl, onReset }) => {
  const utteranceRef = useRef(null);
  const textRef = useRef("");
  const wordsRef = useRef([]);
  const currentWordIndexRef = useRef(0);
  const startedAtRef = useRef(0);
  const elapsedBeforeRef = useRef(0);
  const durationMsRef = useRef(1);
  const intervalRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  const clearTimer = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const setProgressFromElapsed = () => {
    const elapsedMs =
      elapsedBeforeRef.current + (Date.now() - startedAtRef.current);
    const duration = Math.max(1, durationMsRef.current);
    const p = Math.min(1, Math.max(0, elapsedMs / duration));
    setProgress(p);
  };

  const stop = ({ resetElapsed = true } = {}) => {
    try {
      window.speechSynthesis.cancel();
    } catch (e) {}

    clearTimer();
    setIsPlaying(false);
    setIsPaused(false);
    setProgress(0);

    if (resetElapsed) {
      elapsedBeforeRef.current = 0;
      startedAtRef.current = 0;
      currentWordIndexRef.current = 0;
    }
  };

  useEffect(() => {
    stop();
    const cleaned = audioUrl ? cleanText(String(audioUrl)) : "";

    const chunks = splitTextSmart(cleaned);
    textRef.current = chunks.join(" ");

    wordsRef.current = textRef.current.trim()
      ? textRef.current.trim().split(/\s+/)
      : [];
    currentWordIndexRef.current = 0;

    return () => stop();
  }, [audioUrl]);

  const pause = () => {
    if (window.speechSynthesis.speaking && !window.speechSynthesis.paused) {
      window.speechSynthesis.pause();

      elapsedBeforeRef.current += Date.now() - startedAtRef.current;

      clearTimer();
      setProgressFromElapsed();

      setIsPaused(true);
      setIsPlaying(false);
    }
  };

  const speakFromWordIndex = (wordIndex) => {
    if (
      typeof window === "undefined" ||
      !window.speechSynthesis ||
      !window.SpeechSynthesisUtterance
    ) {
      return;
    }

    const words = wordsRef.current;
    const safeIndex = Math.max(
      0,
      Math.min(wordIndex || 0, Math.max(0, words.length - 1)),
    );

    currentWordIndexRef.current = safeIndex;

    const fullText = words.length
      ? words.slice(safeIndex).join(" ")
      : textRef.current;

    const chunks = splitTextSmart(fullText);
    let chunkIndex = 0;

    const rate = 1;
    const chars = fullText.length;
    durationMsRef.current = Math.max(
      1000,
      Math.round((chars / (14 * rate)) * 1000),
    );

    elapsedBeforeRef.current = 0;
    startedAtRef.current = Date.now();

    setIsPlaying(true);
    setIsPaused(false);

    setProgress(words.length ? safeIndex / words.length : 0);

    clearTimer();
    intervalRef.current = setInterval(() => {
      const base = words.length
        ? currentWordIndexRef.current / words.length
        : 0;

      const elapsedMs = Date.now() - startedAtRef.current;
      const within = Math.min(
        1,
        elapsedMs / Math.max(1, durationMsRef.current),
      );

      setProgress(Math.min(1, base + within * (1 - base)));
    }, 120);

    const speakChunk = () => {
      if (chunkIndex >= chunks.length) {
        stop();
        return;
      }

      const utterance = new SpeechSynthesisUtterance(chunks[chunkIndex]);
      utteranceRef.current = utterance;

      utterance.onend = () => {
        chunkIndex++;
        speakChunk();
      };

      utterance.onerror = () => stop();

      utterance.onboundary = (ev) => {
        if (ev && typeof ev.charIndex === "number") {
          const upto = chunks[chunkIndex].slice(0, ev.charIndex);
          const offset = upto.trim() ? upto.trim().split(/\s+/).length - 1 : 0;

          currentWordIndexRef.current = safeIndex + Math.max(0, offset);
        }
      };

      window.speechSynthesis.speak(utterance);
    };

    window.speechSynthesis.cancel();
    speakChunk();
  };

  const resume = () => {
    if (isPaused) {
      window.speechSynthesis.resume();

      startedAtRef.current = Date.now();

      setIsPlaying(true);
      setIsPaused(false);

      clearTimer();
      intervalRef.current = setInterval(() => {
        setProgressFromElapsed();
      }, 120);
    }
  };

  const togglePlay = (e) => {
    e.stopPropagation();

    if (!audioUrl) return;

    if (isPlaying) {
      pause();
      return;
    }

    if (isPaused) {
      resume();
      return;
    }

    speakFromWordIndex(0);
  };

  const handleReset = (e) => {
    e.stopPropagation();
    stop();
    onReset && onReset();
  };

  if (!audioUrl) return null;

  return (
    <div className="voice-message-player">
      <button className="play-btn-small" onClick={togglePlay}>
        {isPlaying ? (
          <PauseIcon fontSize="small" />
        ) : (
          <PlayArrowIcon fontSize="small" />
        )}
      </button>

      <div className="waveform-container">
        <StaticWaveform progress={progress} />
      </div>

      <button className="reset-btn-small" onClick={handleReset}>
        <ReplayIcon fontSize="small" />
      </button>
    </div>
  );
};

const ContentToolbarWrapperComponent = ({
  children,
  onEdit,
  onAdd,
  onReplace,
  onUploadMaterials,
  className = "",
  pageType = "content",
  permission,
  disableAdd = false,
  audioBook,
  setIsAudioGenerated,
  layer,
  id,
  hasContent = true,
  materialType,
}) => {
  // console.log(pageType, "page ");
  const hasAudio = !!audioBook;
  const [loader, setLoader] = useState(false);
  const websocketRef = useRef(null);

  useEffect(() => {
    return () => {
      if (websocketRef.current) {
        try {
          websocketRef.current.close();
        } catch (e) {}
        websocketRef.current = null;
      }
    };
  }, []);

  const handleGenerateAudio = async () => {
    setLoader(true);
    if (!id || !layer) {
      setLoader(false);
      return;
    }
    try {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
      const token = Cookies.get("admin_token");

      websocketRef.current = new WebSocket(GENERATE_AUDIO_BOOK);
      websocketRef.current.onopen = () => {
        const message = {
          field: layer,
          key: id,
          token,
        };
        console.log(message);
        websocketRef.current.send(JSON.stringify(message));
      };

      websocketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data?.status === "completed" || data?.type === "audio") {
            setLoader(false);
            if (setIsAudioGenerated) {
              setIsAudioGenerated(true);
            }
            websocketRef.current?.close();

            setTimeout(() => {
              try {
                window.location.reload();
              } catch (e) {}
            }, 250);
          }
        } catch (err) {
          setLoader(false);
        }
      };
      websocketRef.current.onerror = (err) => {
        console.error("WebSocket error while generating audiobook:", err);
        setLoader(false);
      };

      websocketRef.current.onclose = () => {
        setLoader(false);
      };
    } catch (e) {
      console.error("Failed to start audiobook WebSocket", e);
      setLoader(false);
    }
  };
  const materialTypes = [
    { value: "pdf", label: "PDF" },
    { value: "ppt", label: "PPT" },
    { value: "video", label: "Video" },
    { value: "audio", label: "Audio" },
    { value: "image", label: "Image" },
    { value: "doc", label: "Document" },
    { value: "mindmap", label: "Mindmap" },
  ];
  const addButtonText =
    pageType !== "content" && pageType !== "active_recall_content"
      ? "Add Question"
      : "Add Content";

  const replaceButtonText =
    pageType !== "content" && pageType !== "active_recall_content"
      ? "Replace Question"
      : "Replace Content";

  const isMaterialItem = pageType === "material_item";

  return (
    <div className={`content-toolbar-wrapper ${className}`}>
      <div className={"content-container"}>{children}</div>
      {permission && (onEdit || onAdd || onReplace || onUploadMaterials) && (
        <div className="content-toolbar">
          <div className="content-toolbar-left">
            {hasContent && onEdit && pageType === "content" && (
              <button className="toolbar-button edit-button" onClick={onEdit}>
                <EditIcon className="toolbar-button-icon" />
                Edit Content
              </button>
            )}
            {hasContent && onReplace && (
              <button className="toolbar-button edit-button replace-button" onClick={onReplace}>
                <ReplayIcon className="toolbar-button-icon" />
                {replaceButtonText}
              </button>
            )}
          </div>
          <div className="content-toolbar-middle">
            {hasContent && !hasAudio && pageType === "content" && (
              <button
                type="button"
                className="toolbar-button generate-audio-button"
                onClick={handleGenerateAudio}
                disabled={loader}
              >
                <span>Generate Audiobook</span>
                {loader && <span className="generate-audio-loader" />}
              </button>
            )}
            {hasContent && hasAudio && <WaveformPlayer audioUrl={audioBook} />}
          </div>
          <div className="content-toolbar-right">
            {isMaterialItem && (
              <button
                className="toolbar-button add-button"
                onClick={onUploadMaterials}
                disabled={disableAdd}
              >
                <AddIcon className="toolbar-button-icon" />
                {`Upload ${materialType ? materialTypes.find((t) => t.value === materialType)?.label : "Materials"}`}
              </button>
            )}
            {onAdd && !isMaterialItem && pageType !== "materials" && pageType !== "quiz" && (
              <button
                className="toolbar-button add-button"
                onClick={onAdd}
                disabled={disableAdd}
              >
                <AddIcon className="toolbar-button-icon" />
                {addButtonText}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentToolbarWrapperComponent;
